import React, { useMemo } from 'react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, Text } from '../../components/ui'
import type { PaymentMethod } from '../../types/estoque'
import type { MarketingPaymentSplitMode } from '../../types/marketingNotas'
import {
  SELECT_CLASS,
  formatCurrency,
  emptyPaymentGroup,
  paymentGroupsTotal,
  splitAmountIntoInstallments,
  type PaymentDraftGroup,
} from './notasUi'

type Props = {
  groups: PaymentDraftGroup[]
  methods: PaymentMethod[]
  invoiceTotal: number
  issueDate: string
  onChange: (groups: PaymentDraftGroup[]) => void
}

const NotasPaymentStep: React.FC<Props> = ({
  groups,
  methods,
  invoiceTotal,
  issueDate,
  onChange,
}) => {
  const allocated = useMemo(() => paymentGroupsTotal(groups), [groups])
  const remaining = Math.round((invoiceTotal - allocated) * 100) / 100

  const methodById = useMemo(() => {
    const map = new Map<string, PaymentMethod>()
    for (const method of methods) map.set(method.id, method)
    return map
  }, [methods])

  const updateGroup = (index: number, patch: Partial<PaymentDraftGroup>) => {
    onChange(groups.map((group, i) => (i === index ? { ...group, ...patch } : group)))
  }

  const setMethod = (index: number, methodId: string) => {
    const method = methodById.get(methodId)
    const group = groups[index]
    if (!group) return
    const settlement = method?.settlement || 'avista'
    const amount =
      group.installments.reduce((sum, row) => sum + Number(row.amount || 0), 0) ||
      Math.max(0, remaining + group.installments.reduce((sum, row) => sum + Number(row.amount || 0), 0))
    const due = group.installments[0]?.due_date || issueDate || new Date().toISOString().slice(0, 10)

    if (settlement === 'avista') {
      updateGroup(index, {
        payment_method_id: methodId,
        split_mode: 'single',
        installments: [{ installment_number: 1, amount, due_date: due }],
      })
      return
    }

    updateGroup(index, {
      payment_method_id: methodId,
      split_mode: group.split_mode === 'auto' ? 'auto' : group.installments.length > 1 ? 'manual' : 'single',
    })
  }

  const setSplitMode = (index: number, mode: MarketingPaymentSplitMode) => {
    const group = groups[index]
    if (!group) return
    const method = methodById.get(group.payment_method_id)
    if (method?.settlement === 'avista' && mode !== 'single') return

    const totalAmount = group.installments.reduce((sum, row) => sum + Number(row.amount || 0), 0)
    const firstDue =
      group.installments[0]?.due_date || issueDate || new Date().toISOString().slice(0, 10)

    if (mode === 'single') {
      updateGroup(index, {
        split_mode: 'single',
        installments: [
          {
            installment_number: 1,
            amount: totalAmount || Math.max(0, remaining + totalAmount),
            due_date: firstDue,
          },
        ],
      })
      return
    }

    if (mode === 'auto') {
      const count = Math.max(2, group.installments.length)
      updateGroup(index, {
        split_mode: 'auto',
        installments: splitAmountIntoInstallments(totalAmount || remaining + totalAmount, count, firstDue),
      })
      return
    }

    updateGroup(index, {
      split_mode: 'manual',
      installments:
        group.installments.length > 0
          ? group.installments
          : [{ installment_number: 1, amount: totalAmount, due_date: firstDue }],
    })
  }

  const applyAutoSplit = (index: number, count: number, firstDueDate: string) => {
    const group = groups[index]
    if (!group) return
    const totalAmount = group.installments.reduce((sum, row) => sum + Number(row.amount || 0), 0)
    updateGroup(index, {
      split_mode: 'auto',
      installments: splitAmountIntoInstallments(
        totalAmount > 0 ? totalAmount : Math.max(0, remaining + totalAmount),
        count,
        firstDueDate
      ),
    })
  }

  const updateInstallment = (
    groupIndex: number,
    installmentIndex: number,
    patch: Partial<{ amount: number; due_date: string }>
  ) => {
    const group = groups[groupIndex]
    if (!group) return
    updateGroup(groupIndex, {
      installments: group.installments.map((row, i) =>
        i === installmentIndex ? { ...row, ...patch } : row
      ),
    })
  }

  const addInstallment = (groupIndex: number) => {
    const group = groups[groupIndex]
    if (!group) return
    const last = group.installments[group.installments.length - 1]
    updateGroup(groupIndex, {
      split_mode: 'manual',
      installments: [
        ...group.installments,
        {
          installment_number: group.installments.length + 1,
          amount: 0,
          due_date: last?.due_date || issueDate || new Date().toISOString().slice(0, 10),
        },
      ],
    })
  }

  const removeInstallment = (groupIndex: number, installmentIndex: number) => {
    const group = groups[groupIndex]
    if (!group || group.installments.length <= 1) return
    updateGroup(groupIndex, {
      split_mode: 'manual',
      installments: group.installments
        .filter((_, i) => i !== installmentIndex)
        .map((row, i) => ({ ...row, installment_number: i + 1 })),
    })
  }

  const removeGroup = (index: number) => {
    if (groups.length <= 1) return
    onChange(groups.filter((_, i) => i !== index))
  }

  const addGroup = () => {
    onChange([...groups, emptyPaymentGroup(Math.max(0, remaining), issueDate)])
  }

  return (
    <div className="space-y-4">
      <div>
        <Heading as="h4" size="xl" className="text-gray-900 dark:text-white">
          Formas de pagamento
        </Heading>
        <Text size="sm" color="muted" className="mt-1">
          A soma das parcelas deve fechar o total da nota. É possível combinar mais de uma forma.
        </Text>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900/40">
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          <span className="text-gray-700 dark:text-gray-200">
            Total da nota: <strong>{formatCurrency(invoiceTotal)}</strong>
          </span>
          <span className="text-gray-700 dark:text-gray-200">
            Alocado: <strong>{formatCurrency(allocated)}</strong>
          </span>
          <span
            className={
              Math.abs(remaining) < 0.009
                ? 'text-green-700 dark:text-green-300'
                : 'text-amber-700 dark:text-amber-300'
            }
          >
            Restante: <strong>{formatCurrency(remaining)}</strong>
          </span>
        </div>
      </div>

      {groups.map((group, groupIndex) => {
        const method = methodById.get(group.payment_method_id)
        const allowsInstallments = method?.settlement === 'parcelado'
        const groupTotal = group.installments.reduce((sum, row) => sum + Number(row.amount || 0), 0)

        return (
          <div
            key={group.key}
            className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
                Forma {groupIndex + 1}
              </Text>
              <button
                type="button"
                onClick={() => removeGroup(groupIndex)}
                disabled={groups.length <= 1}
                className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 dark:hover:bg-red-900/20"
                title="Remover forma"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  Forma de pagamento *
                </label>
                <select
                  className={SELECT_CLASS + ' w-full'}
                  value={group.payment_method_id}
                  onChange={(e) => setMethod(groupIndex, e.target.value)}
                >
                  <option value="">Selecione</option>
                  {methods.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                      {m.settlement === 'parcelado' ? ' (aceita parcelamento)' : ' (somente à vista)'}
                    </option>
                  ))}
                </select>
              </div>

              {allowsInstallments && (
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Como lançar
                  </label>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-700 dark:text-gray-200">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        checked={group.split_mode === 'single'}
                        onChange={() => setSplitMode(groupIndex, 'single')}
                      />
                      Uma parcela
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        checked={group.split_mode === 'manual'}
                        onChange={() => setSplitMode(groupIndex, 'manual')}
                      />
                      Parcelas manuais
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        checked={group.split_mode === 'auto'}
                        onChange={() => setSplitMode(groupIndex, 'auto')}
                      />
                      Dividir automaticamente
                    </label>
                  </div>
                </div>
              )}

              {allowsInstallments && group.split_mode === 'auto' && (
                <div className="sm:col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Nº de parcelas
                    </label>
                    <Input
                      type="number"
                      min={2}
                      step={1}
                      value={group.installments.length}
                      onChange={(e) => {
                        const count = Math.max(2, Number(e.target.value) || 2)
                        applyAutoSplit(
                          groupIndex,
                          count,
                          group.installments[0]?.due_date ||
                            issueDate ||
                            new Date().toISOString().slice(0, 10)
                        )
                      }}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      1º vencimento
                    </label>
                    <Input
                      type="date"
                      value={group.installments[0]?.due_date || ''}
                      onChange={(e) =>
                        applyAutoSplit(groupIndex, group.installments.length, e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Valor do grupo
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step="any"
                      value={groupTotal}
                      onChange={(e) => {
                        const amount = e.target.value === '' ? 0 : Number(e.target.value)
                        updateGroup(groupIndex, {
                          split_mode: 'auto',
                          installments: splitAmountIntoInstallments(
                            amount,
                            group.installments.length,
                            group.installments[0]?.due_date ||
                              issueDate ||
                              new Date().toISOString().slice(0, 10)
                          ),
                        })
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between">
                <Text size="xs" className="font-medium uppercase tracking-wide text-gray-500">
                  Parcelas · {formatCurrency(groupTotal)}
                </Text>
                {allowsInstallments && group.split_mode === 'manual' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addInstallment(groupIndex)}
                    className="inline-flex items-center gap-1"
                  >
                    <PlusIcon className="h-3.5 w-3.5" />
                    Parcela
                  </Button>
                )}
              </div>

              {group.installments.map((row, installmentIndex) => (
                <div
                  key={`${group.key}-${installmentIndex}`}
                  className="grid grid-cols-1 gap-2 sm:grid-cols-[80px_1fr_1fr_auto]"
                >
                  <Input value={`${row.installment_number}ª`} disabled />
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    value={row.amount}
                    disabled={group.split_mode === 'auto'}
                    onChange={(e) =>
                      updateInstallment(groupIndex, installmentIndex, {
                        amount: e.target.value === '' ? 0 : Number(e.target.value),
                      })
                    }
                  />
                  <Input
                    type="date"
                    value={row.due_date}
                    disabled={group.split_mode === 'auto' && installmentIndex > 0}
                    onChange={(e) => {
                      if (group.split_mode === 'auto') {
                        applyAutoSplit(groupIndex, group.installments.length, e.target.value)
                      } else {
                        updateInstallment(groupIndex, installmentIndex, {
                          due_date: e.target.value,
                        })
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeInstallment(groupIndex, installmentIndex)}
                    disabled={
                      group.installments.length <= 1 ||
                      group.split_mode === 'auto' ||
                      !allowsInstallments
                    }
                    className="inline-flex items-center justify-center rounded-lg px-2 py-2 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 dark:hover:bg-red-900/20"
                    title="Remover parcela"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )
      })}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addGroup}
        className="inline-flex items-center gap-1"
      >
        <PlusIcon className="h-4 w-4" />
        Adicionar Forma
      </Button>
    </div>
  )
}

export default NotasPaymentStep
